import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { PawPrint as PawPrintLucide, Plus, Edit, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { useSafePostHog } from '../components/PostHogProvider';

const CatProfiles = () => {
  const posthog = useSafePostHog();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({ name: '', breed: '', age: '', weight: '', health_notes: '', avatar_url: '' });

  const defaultAvatars = [
    { url: '/avatars/ginger-cat.png', label: 'Ginger' },
    { url: '/avatars/siamese-cat.png', label: 'Siamese' },
    { url: '/avatars/tabby-cat.png', label: 'Tabby' }
  ];

  useEffect(() => { loadProfiles(); }, []);

  const loadProfiles = async () => {
    try { const r = await api.get('/cat-profiles'); setProfiles(r.data); }
    catch { toast.error('Failed to load profiles'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProfile) { await api.put(`/cat-profiles/${editingProfile.id}`, formData); toast.success('Profile updated!'); }
      else { await api.post('/cat-profiles', formData); toast.success('Profile created!'); }
      posthog.capture('profile_updated', {
        action: editingProfile ? 'edit' : 'create',
        cat_name: formData.name,
        has_breed: !!formData.breed,
        has_health_notes: !!formData.health_notes,
      });
      setDialogOpen(false); resetForm(); loadProfiles();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save profile'); }
  };

  const handleEdit = (p) => {
    setEditingProfile(p);
    setFormData({ name: p.name || '', breed: p.breed || '', age: p.age?.toString() || '', weight: p.weight?.toString() || '', health_notes: p.health_notes || '', avatar_url: p.avatar_url || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cat profile?')) return;
    try { await api.delete(`/cat-profiles/${id}`); toast.success('Profile deleted'); loadProfiles(); }
    catch { toast.error('Failed to delete profile'); }
  };

  const resetForm = () => { setFormData({ name: '', breed: '', age: '', weight: '', health_notes: '', avatar_url: '' }); setEditingProfile(null); };

  if (loading) {
    return <DashboardLayout title="MY CATS"><div className="flex items-center justify-center py-32"><div className="w-10 h-10 border-[3px] border-[#1B4332] border-t-transparent rounded-full animate-spin"></div></div></DashboardLayout>;
  }

  return (
    <DashboardLayout title="MY CATS">
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogTrigger asChild>
          <Button className="mb-8 rounded-full font-bold bg-[#1B4332] hover:bg-[#1B4332]/90 text-white px-6" data-testid="add-cat-btn">
            <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} /> Add Cat Profile
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl rounded-none sm:rounded-2xl border-2 border-[#1B4332]/10">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {editingProfile ? 'EDIT PROFILE' : 'NEW CAT PROFILE'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#FFFDF7] border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-11" required data-testid="cat-name-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Breed</Label>
                <Input value={formData.breed} onChange={(e) => setFormData({ ...formData, breed: e.target.value })} className="bg-[#FFFDF7] border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-11" data-testid="cat-breed-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Age (years)</Label>
                <Input type="number" value={formData.age} onChange={(e) => setFormData({ ...formData, age: e.target.value })} className="bg-[#FFFDF7] border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-11" data-testid="cat-age-input" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Weight (lbs)</Label>
              <Input type="number" step="0.1" value={formData.weight} onChange={(e) => setFormData({ ...formData, weight: e.target.value })} className="bg-[#FFFDF7] border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-11" data-testid="cat-weight-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Health Notes</Label>
              <Textarea value={formData.health_notes} onChange={(e) => setFormData({ ...formData, health_notes: e.target.value })} className="bg-[#FFFDF7] border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] min-h-[100px]" placeholder="Allergies, conditions, dietary needs..." data-testid="cat-health-notes-input" />
            </div>
            <div className="mb-2">
              <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Profile Picture</Label>
              <div className="grid grid-cols-3 gap-4 mt-3 max-w-sm mx-auto">
                {defaultAvatars.map((avatar, idx) => (
                  <div
                    key={idx}
                    onClick={() => setFormData({ ...formData, avatar_url: avatar.url })}
                    className={`cursor-pointer rounded-2xl overflow-hidden border-4 transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg ${formData.avatar_url === avatar.url ? 'border-[#1B4332] ring-2 ring-[#1B4332]/20 shadow-lg' : 'border-transparent'}`}
                    data-testid={`avatar-option-${idx}`}
                  >
                    <img src={avatar.url} alt={`Cat avatar ${idx + 1}`} className="w-full aspect-square object-cover" />
                  </div>
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full rounded-full font-bold bg-[#1B4332] hover:bg-[#1B4332]/90 text-white h-12 text-base hover:-translate-y-0.5 transition-all" data-testid="save-cat-profile-btn">
              {editingProfile ? 'Update Profile' : 'Create Profile'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {profiles.length === 0 ? (
        <div className="bg-white border-2 border-[#1B4332]/8 rounded-2xl p-8 sm:p-16 text-center" data-testid="empty-state">
          <PawPrintLucide className="w-20 h-20 text-[#1B4332]/10 mx-auto mb-4" strokeWidth={2.5} />
          <h3 className="text-2xl font-black text-[#1B4332] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>NO CATS YET</h3>
          <p className="text-[#1B4332]/50 mb-6">Add your first cat profile to get personalized food recommendations</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {profiles.map((p) => (
            <div key={p.id} className="bg-white border-2 border-[#1B4332]/8 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`cat-profile-card-${p.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#BFDB38]/10 to-[#1B4332]/5">
                {p.avatar_url ? (
                  <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PawPrintLucide className="w-20 h-20 text-[#1B4332]/10" strokeWidth={2.5} />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="text-xl font-black text-[#1B4332] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>{p.name}</h3>
                {p.breed && <p className="text-xs text-[#1B4332]/60 mb-1"><span className="font-bold text-[#FF6B6B]">BREED</span> {p.breed}</p>}
                {p.age && <p className="text-xs text-[#1B4332]/60 mb-1"><span className="font-bold text-[#FF6B6B]">AGE</span> {p.age} years</p>}
                {p.weight && <p className="text-xs text-[#1B4332]/60 mb-3"><span className="font-bold text-[#FF6B6B]">WEIGHT</span> {p.weight} lbs</p>}
                {p.health_notes && <p className="text-xs text-[#1B4332]/50 mb-4 line-clamp-2">{p.health_notes}</p>}
                <div className="flex gap-2">
                  <Button onClick={() => handleEdit(p)} variant="outline" size="sm" className="flex-1 rounded-full font-bold border-2 border-[#1B4332]/10" data-testid={`edit-cat-${p.id}`}>
                    <Edit className="w-4 h-4 mr-2" strokeWidth={2.5} /> Edit
                  </Button>
                  <Button onClick={() => handleDelete(p.id)} variant="destructive" size="sm" className="rounded-full bg-[#FF6B6B] hover:bg-[#FF6B6B]/90" data-testid={`delete-cat-${p.id}`}>
                    <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
};

export default CatProfiles;
